import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Waypoint from 'react-waypoint'
import classNames from 'classnames/bind'
import { useAtomValue, useSetAtom } from 'jotai'
import { useGetFile } from '../../../../../hooks/queries/files'
import {
  getFileKind,
  isImageExtension,
  isPdfExtension,
  isPrintableFileExtension,
} from '../../../../../misc/files.utilities'
import { MedicalEventContentTypeIcon } from '../../../../../model/MedicalEvent'
import { IconsType, Loader } from '../../../../shared'
import { EventFileProps } from '../PatientTransversalNavigationEventDetails.model'
import {
  EventDocumentType,
  PatientTransversalNavigationEventDocHeader,
} from '../PatientTransversalNavigationEventDocHeader'
import { FileViewer } from '../../../../file'
import { useScrollToEventDocOrFile } from '../useScrollToEventDocOrFile.hook'
import { BackgroundColors } from '../../../../file/FileViewer/FileViewer.model'
import styles from './PatientTransversalNavigationEventFile.module.scss'
import {
  exitFullScreenAtom,
  scrolledItemIdAtom,
  ScrollHighlightStatus,
  scrollHighlightStatusAtom,
} from '../../../../../state/navTrans'

import { isDefined } from '../../../../../misc/functions.utilities'

const cx = classNames.bind(styles)

export const PatientTransversalNavigationEventFile = ({
  id,
  isSelected,
  isFirstDocOrFile,
  isEventEditable,
  allowWaypointDetection,
  print,
  isPrinting,
                                                        currentPrintingItems,
}: EventFileProps) => {
  const scrolledDocOrFileId = useAtomValue(scrolledItemIdAtom)
  const setScrolledDocOrFileId = useSetAtom(scrolledItemIdAtom)
  const setScrollHighlightStatus = useSetAtom(scrollHighlightStatusAtom)
  const setExitFullScreenState = useSetAtom(exitFullScreenAtom)

  const location = useLocation()
  const navigate = useNavigate()

  const {
    query: { data: file, isLoading },
  } = useGetFile({ fileId: id })
  useScrollToEventDocOrFile(id, EventDocumentType.File, isLoading, isSelected, isFirstDocOrFile)

  const handlePrint = useCallback(() => !!file && print({ files: [file] }), [print, file])

  const handleVisibleWaypoint = useCallback(
    () => allowWaypointDetection && setScrolledDocOrFileId(id),
    [allowWaypointDetection, setScrolledDocOrFileId, id],
  )

  const focused = useMemo(
    () => (isSelected && scrolledDocOrFileId === id) || scrolledDocOrFileId === id,
    [id, isSelected, scrolledDocOrFileId],
  )

  const handleEnterFullScreen = useCallback(() => {
    setScrollHighlightStatus(ScrollHighlightStatus.DISABLED)
    if (scrolledDocOrFileId !== id) {
      navigate(`${location.search}#file=${id}`)
    }
  }, [id, location.search, navigate, scrolledDocOrFileId, setScrollHighlightStatus])

  const handleExitFullScreen = useCallback(() => {
    isSelected && setExitFullScreenState(true)
  }, [isSelected, setExitFullScreenState])

  useEffect(() => {
    return () => setScrolledDocOrFileId(null)
  }, [setScrolledDocOrFileId])

  if (isLoading) return <Loader />
  if (!file) return <p>Impossible de récupérer le fichier</p>

  return (
    <div
      id={`file-${id}`}
      className={cx({
        image: isImageExtension(file.extension),
        pdf: isPdfExtension(file.extension),
      })}
    >
      <PatientTransversalNavigationEventDocHeader
        id={file.id}
        title={`${file.visibleName} ${file.version && (file.version>1 || (isDefined(file.isOpenable) && !file.isOpenable))? `(V${file.version})` : ''}`}
        icon={
          (file?.documentCategory?.icon as IconsType) ||
          MedicalEventContentTypeIcon[getFileKind(file.extension)]
        }
        type={EventDocumentType.File}
        isEventEditable={isEventEditable}
        onPrint={isPrintableFileExtension(file.extension) ? handlePrint : undefined}
        isDisabled={isPrinting}
        isPrinting={
          !!(
            isPrinting &&
            currentPrintingItems &&
            currentPrintingItems?.find((item) => item?.id === file.id && item.kind === 'file')
          )
        }
        isOpenable={file.isOpenable}
      />
      <Waypoint key={id} bottomOffset="65%" topOffset="20%" onEnter={handleVisibleWaypoint} />
      <div className={cx('fileContainer', { focused })}>
        {file?.isOpenable && (
          <FileViewer
            file={file}
            backgroundColor={BackgroundColors.white}
            onEnterFullScreen={handleEnterFullScreen}
            onExitFullScreen={handleExitFullScreen}
          />
        )}
      </div>
      {!file?.isOpenable && (
        <div
          className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300"
          role="alert"
        >
          <span className="font-medium">
            {`Une version plus récente de ce ${file?.visibleName?.toLowerCase()?.replace('.', '')} est disponible.`}
          </span>
        </div>
      )}
    </div>
  )
}
